import React, { ReactElement, useEffect, useState } from 'react'
import { GET } from '@utils/api'
import { makeStyles } from 'tss-react/mui'

import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'

import useSelector from '@hooks/use-selector'
import { AppDispatch, ProductTypeType, PromotionType } from '@types'
import { getAttributes } from '@store/actions/attributes'
import {
  selectAttribute,
  selectAttributesByProductType,
} from '@store/selectors/attributes'
import { useDispatch } from 'react-redux'

type PropsType = {
  promotion: PromotionType | null
  editing: boolean
  onConfigurationChange: (object) => void
}

type ConfigurationType = {
  attribute_value_qualifying_match: number
  attribute_value_discount_match: number
}

const useStyles = makeStyles()((theme) => ({
  paperWrapper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}))

const AttributeUpgrade = ({
  promotion,
  editing,
  onConfigurationChange,
}: PropsType): ReactElement => {
  const dispatch = useDispatch<AppDispatch>()
  const { classes } = useStyles()
  const [qualifyingMatch, setQualifyingMatch] = useState<number | undefined>(
    undefined,
  )
  const [discountMatch, setDiscountMatch] = useState<number | undefined>(
    undefined,
  )
  const [productType, setProductType] = useState<ProductTypeType | undefined>(
    undefined,
  )
  const [selectedAttribute, setSelectedAttribute] = useState(0)

  const attributes = useSelector(selectAttributesByProductType, productType)
  const attribute = useSelector(selectAttribute, selectedAttribute)
  const attributeValues = attribute?.attribute_values

  useEffect(() => {
    dispatch(getAttributes())
  }, [productType])

  useEffect(() => {
    const newConfig: ConfigurationType = {
      attribute_value_qualifying_match: qualifyingMatch ?? 0,
      attribute_value_discount_match: discountMatch ?? 0,
    }
    onConfigurationChange(newConfig)
  }, [discountMatch, qualifyingMatch])

  useEffect(() => {
    if (!promotion) return
    setQualifyingMatch(
      promotion.configuration.attribute_value_qualifying_match ?? 0,
    )
    setDiscountMatch(
      promotion.configuration.attribute_value_discount_match ?? 0,
    )
    if (promotion.configuration.attribute_value_qualifying_match) {
      GET(
        `/p4/attributeValues/${promotion.configuration.attribute_value_qualifying_match}`,
      ).then(({ data: { attribute_id } }) => {
        setSelectedAttribute(attribute_id)
      })
    }
  }, [promotion])

  const handleProductTypeChange = ({
    target: { value },
  }: SelectChangeEvent) => {
    setQualifyingMatch(undefined)
    setDiscountMatch(undefined)
    setSelectedAttribute(0)
    setProductType(value as ProductTypeType)
  }

  const handleAttributeChange = ({
    target: { value },
  }: SelectChangeEvent<number>) => {
    setQualifyingMatch(undefined)
    setDiscountMatch(undefined)
    setSelectedAttribute(value as number)
  }

  const handleSelectQualifyingMatch = ({
    target: { value },
  }: SelectChangeEvent<number>) => {
    setQualifyingMatch(value as number)
  }

  const handleSelectDiscountMatch = ({
    target: { value },
  }: SelectChangeEvent<number>) => {
    setDiscountMatch(value as number)
  }

  return (
    <Paper className={classes.paperWrapper}>
      <Typography variant='h5'>Attribute Upgrade</Typography>

      <FormControl fullWidth sx={{ mt: 2 }}>
        <InputLabel>Product Type</InputLabel>
        <Select
          disabled={!editing}
          fullWidth
          onChange={handleProductTypeChange}
          value={productType}
        >
          <MenuItem value='beverage'>Beverage</MenuItem>
          <MenuItem value='bag'>Bag</MenuItem>
          <MenuItem value='food'>Food</MenuItem>
          <MenuItem value='bottle'>Bottle</MenuItem>
          <MenuItem value='merchandise'>Merchandise</MenuItem>
          <MenuItem value='togo'>To-Go</MenuItem>
        </Select>
        <FormHelperText>
          First select a product type to begin narrowing down your attribute
          upgrade options.
        </FormHelperText>
      </FormControl>

      {productType && (
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Attribute</InputLabel>
          <Select
            disabled={!editing}
            onChange={handleAttributeChange}
            value={selectedAttribute}
          >
            {attributes.map((attribute) => (
              <MenuItem key={attribute.id} value={attribute.id}>
                {attribute.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Next select an attribute, after you will pick two different values
            of the attribute.
          </FormHelperText>
        </FormControl>
      )}

      {attributeValues && (
        <>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Qualifying Attribute Value (Get This)</InputLabel>
            <Select
              disabled={!editing}
              onChange={handleSelectQualifyingMatch}
              value={qualifyingMatch}
            >
              {attributeValues.map((av) => (
                <MenuItem key={av.id} value={av.id}>
                  {av.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              This is the attribute value to be discounted. This would be the
              more expensive option you are letting the customer qualify to get
              a discount on.
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Discount Attribute Value (For This Price)</InputLabel>
            <Select
              disabled={!editing}
              onChange={handleSelectDiscountMatch}
              value={discountMatch}
            >
              {attributeValues.map((av) => (
                <MenuItem key={av.id} value={av.id}>
                  {av.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              This is the attribute value to discount the price to. This would
              be the less expensive option you are letting the customer pay the
              equivalent of.
            </FormHelperText>
          </FormControl>
        </>
      )}
    </Paper>
  )
}

export default AttributeUpgrade
